/**
 * Espeja la forma real del SchemaJson que devuelve GET /formularios/vigente
 * (verificado en vivo contra QAS, sociedad CONS-KAUF, ago-2026). Se define acá
 * aunque hoy el widget use datos de ejemplo embebidos: cuando se conecte al
 * backend real, es un cambio de "de dónde viene el dato", no de forma.
 */

export interface ConsentimientoCampo {
  id: string;
  label: string;
  placeholder: string;
  required: boolean;
  /** Columna del grid (1 o 2) declarada por el schema — a pedido explícito
   * (28-ago-2026), el layout tiene que salir de acá, no de un orden fijo en
   * el código. Si el schema no la trae, se infiere por orden (ver
   * WidgetComponent.filasCampos). */
  column: number | null;
}

export interface ConsentimientoLinkedResource {
  label: string;
  url: string | null;
}

export interface ConsentimientoFinalidad {
  code: string;
  id: string;
  label: string;
  description: string;
  linkedResource: ConsentimientoLinkedResource | null;
}

export interface ConsentimientoAcceptanceLink {
  id: string;
  label: string;
  url: string | null;
}

export interface ConsentimientoSchema {
  title: string;
  subtitle: string;
  company: string;
  brandAlias: string;
  requiredFieldsNote: string;
  camposPersonales: ConsentimientoCampo[];
  /** Texto del checkbox principal (`mainConsent` en el SchemaJson real) —
   * a pedido explícito (07-sep-2026), se muestra siempre visible, al mismo
   * nivel que los campos personales, en vez de escondido dentro del
   * acordeón "Quiero ver más información" — así se ve igual que en el
   * widget de Bloomreach (captura real revisada en maxus.cl). Controla el
   * mismo estado que las finalidades adicionales individuales (ver
   * ConsentimientoWidgetComponent.toggleSeleccionarTodas). Nunca marcado por
   * defecto, aunque el SchemaJson real declare `mainConsent.default: true`
   * — decisión explícita del usuario, no error. */
  mainConsentLabel: string;
  legalIntro: string;
  finalidadesNecesariasLabel: string;
  finalidadesNecesarias: string[];
  finalidadesAdicionalesLabel: string;
  finalidadesAdicionalesHelperText: string;
  finalidadesAdicionales: ConsentimientoFinalidad[];
  acceptanceText: string;
  acceptanceLinks: ConsentimientoAcceptanceLink[];
  footerLegalNote: string;
}

/**
 * Datos de ejemplo — copia fiel del SchemaJson real de Kaufmann (sociedad
 * CONS-KAUF) leído de la base QAS esta misma sesión, incluyendo las
 * correcciones de texto ("Sí Autorizo...") ya aplicadas a FIN-001/002/003.
 */
export const CONSENTIMIENTO_SCHEMA_EJEMPLO: ConsentimientoSchema = {
  title: 'Mantengamos el contacto',
  subtitle:
    'Para continuar compartiendo información, novedades y servicios diseñados exclusivamente para ti.',
  company: 'Motores de los Andes Vehículos Motorizados SpA',
  brandAlias: 'Andes Retail',
  requiredFieldsNote: 'Los campos marcados con asterisco son obligatorios',
  mainConsentLabel: 'Autorizo el envío de comunicaciones comerciales y el tratamiento de mis datos',
  camposPersonales: [
    { id: 'nombre', label: 'Nombre', placeholder: 'Ingrese sus nombres', required: true, column: 1 },
    { id: 'apellido', label: 'Apellido', placeholder: 'Ingrese sus apellidos', required: true, column: 2 },
    // A pedido explícito (28-ago-2026): el ejemplo del campo se muestra sin puntos, consistente
    // con que el backend acepta y normaliza el RUT sin puntos en los 3 endpoints que lo reciben.
    { id: 'rut_cliente', label: 'RUT Cliente', placeholder: '12345678-9', required: true, column: 1 },
    { id: 'correo', label: 'Correo electrónico', placeholder: 'usuario@ejemplo.cl', required: true, column: 2 },
    // A pedido explícito (28-ago-2026): es solo para Chile, no hace falta mostrar +56.
    { id: 'telefono', label: 'Teléfono', placeholder: '9 1234 5678', required: false, column: 1 },
  ],
  legalIntro:
    "En cumplimiento de lo dispuesto en la Ley N° 19.628 y sus modificaciones, como titular identificado al final de este documento, autorizo de manera libre, informada, específica e inequívoca a {{company}} (en adelante, {{brandAlias}} o el 'Responsable') para tratar mis datos personales conforme a las siguientes finalidades:",
  finalidadesNecesariasLabel: '1.- FINALIDADES NECESARIAS.',
  finalidadesNecesarias: [
    'De acuerdo a lo establecido en la política de privacidad, entiendo que el tratamiento de mis datos personales de identificación, contacto, y todos aquellos que sean estrictamente necesarios para la gestión de mi solicitud, la entrega de productos y la prestación de los servicios contratados.',
    'Junto con lo anterior, declaro estar en conocimiento que el Responsable podría requerir de estos u otros datos personales para dar cumplimiento a sus obligaciones legales en ocasión de la gestión de mi solicitud.',
  ],
  finalidadesAdicionalesLabel: '2.- FINALIDADES ADICIONALES.',
  finalidadesAdicionalesHelperText:
    'Marque las casillas de su preferencia. El no marcar estas casillas no condicionará la prestación de los servicios principales.',
  finalidadesAdicionales: [
    {
      code: 'FIN-001',
      id: 'comunicaciones_comerciales',
      label: 'Comunicaciones comerciales',
      description:
        'Sí Autorizo el envío de información publicitaria, promociones y ofertas sobre productos y servicios de Andes Retail y sus empresas relacionadas informadas en la Política de Privacidad de Andes Retail, a través de correo electrónico, SMS, Redes Sociales oficiales del Responsable o llamadas telefónicas.',
      linkedResource: null,
    },
    {
      code: 'FIN-002',
      id: 'tratamiento_grupo_kaufmann',
      label: 'Sí Autorizo el tratamiento de mis datos',
      description:
        'por Andes Retail y las empresas del grupo Kaufmann identificadas en el siguiente listado, las cuales declaro conocer y aceptar.',
      linkedResource: { label: 'listado', url: null },
    },
    {
      code: 'FIN-003',
      id: 'analisis_comportamiento',
      label: 'Análisis de comportamiento',
      description:
        'Sí Autorizo el tratamiento de mis datos de navegación en sitios web, aplicaciones y herramientas tecnológicas de Andes Retail y empresas relacionadas para la elaboración de perfiles y análisis de preferencias.',
      linkedResource: null,
    },
    {
      code: 'FIN-004',
      id: 'fines_estadisticos_investigacion',
      label: 'Fines estadísticos o de investigación',
      description:
        'Los datos podrán ser utilizados con fines estadísticos o de investigación y desarrollo de productos u otros servicios que, en conformidad a su giro, Andes Retail o sus empresas relacionadas informadas en la Política de Privacidad, pueda ofrecer a sus clientes.',
      linkedResource: null,
    },
  ],
  acceptanceText:
    'Con todo, declaro que he tenido acceso, leído y acepto las {{link:politicas_privacidad}}, y {{link:condiciones_concurso}}. Asimismo, reconozco que he sido informado respecto a los derechos de los que dispongo, a saber, el derecho de acceso, rectificación, cancelación, oposición y portabilidad.',
  acceptanceLinks: [
    { id: 'politicas_privacidad', label: 'Políticas de Privacidad de Andes Retail', url: 'https://example.com/politicas-privacidad' },
    { id: 'condiciones_concurso', label: 'condiciones del concurso', url: null },
  ],
  footerLegalNote: 'Sus datos serán tratados conforme a la Ley N° 19.628 de Protección de Datos Personales.',
};

/** Forma cruda mínima del SchemaJson tal como lo devuelve el backend
 * (JSON.parse de FormularioVigenteResponse.SchemaJson) — solo los campos
 * que efectivamente lee mapRawSchemaJson, sin `any`. */
interface RawSchemaJson {
  title: string;
  subtitle: string;
  meta?: { company?: string; brandAlias?: string; requiredFieldsNote?: string };
  sections?: RawSchemaSection[];
  footer?: { legalNotice?: { text?: string } };
}

interface RawSchemaSection {
  type: string;
  fields?: RawSchemaField[];
  intro?: { text?: string };
  mainConsent?: { label?: string };
  subsections?: RawSchemaSubsection[];
  acceptance?: { text?: string; links?: RawSchemaAcceptanceLink[] };
}

interface RawSchemaField {
  id: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  column?: number;
}

interface RawSchemaSubsection {
  type: string;
  label?: string;
  content?: string[];
  helperText?: string;
  selectAllOption?: { id: string; label: string };
  /** Forma vieja: arreglo plano con todas las finalidades juntas. El backend ya no la manda
   * para GET /formularios/vigente (ver `finalidades` abajo), pero se deja como fallback --
   * bajo costo, cero downside. */
  options?: RawSchemaFinalidad[];
  /** A pedido explícito (17-sep-2026, backend): cada finalidad agrupada en su propio bloque
   * `{ options: [una sola] }` en vez de todas juntas en un solo arreglo plano -- forma real que
   * manda hoy GET /formularios/vigente. */
  finalidades?: { options?: RawSchemaFinalidad[] }[];
}

interface RawSchemaFinalidad {
  code: string;
  id: string;
  label: string;
  description?: string;
  linkedResource?: { label: string; url: string | null } | null;
}

interface RawSchemaAcceptanceLink {
  id: string;
  label: string;
  url?: string | null;
}

/**
 * Convierte el SchemaJson crudo del backend (forma real, con sections[] /
 * subsections[], verificada en vivo contra QAS) a la forma plana que
 * consume el widget. Si el shape no calza con lo esperado (versión más
 * nueva del schema, campo renombrado) lanza en vez de armar un widget a
 * medias con datos silenciosamente incompletos.
 */
export function mapRawSchemaJson(raw: RawSchemaJson): ConsentimientoSchema {
  const seccionDatos = raw.sections?.find((s) => s.type === 'fields');
  const seccionLegal = raw.sections?.find((s) => s.type === 'legal');
  if (!seccionDatos || !seccionLegal) {
    throw new Error('El SchemaJson no tiene la forma esperada (falta sección "fields" o "legal").');
  }

  const subNecesarias = seccionLegal.subsections?.find((s) => s.type === 'accordion');
  const subAdicionales = seccionLegal.subsections?.find((s) => s.type === 'checkbox_group');
  if (!subNecesarias || !subAdicionales) {
    throw new Error('El SchemaJson no tiene las dos subsecciones esperadas (accordion / checkbox_group).');
  }

  return {
    title: raw.title,
    subtitle: raw.subtitle,
    company: raw.meta?.company ?? '',
    brandAlias: raw.meta?.brandAlias ?? '',
    requiredFieldsNote: raw.meta?.requiredFieldsNote ?? '',
    // Preferimos mainConsent.label (lo que trae Kaufmann); si no viene, caemos
    // al selectAllOption.label de la subsección (lo que trae Andes Motor en
    // vez de mainConsent) — dos nombres distintos para el mismo texto según
    // qué sociedad armó el SchemaJson.
    mainConsentLabel: seccionLegal.mainConsent?.label ?? subAdicionales.selectAllOption?.label ?? 'Autorizo el tratamiento de mis datos',
    camposPersonales: (seccionDatos.fields ?? []).map((f) => ({
      id: f.id,
      label: f.label,
      placeholder: f.placeholder ?? '',
      required: !!f.required,
      column: f.column ?? null,
    })),
    legalIntro: seccionLegal.intro?.text ?? '',
    finalidadesNecesariasLabel: subNecesarias.label ?? '',
    finalidadesNecesarias: subNecesarias.content ?? [],
    finalidadesAdicionalesLabel: subAdicionales.label ?? '',
    finalidadesAdicionalesHelperText: subAdicionales.helperText ?? '',
    // A pedido explícito (25-sep-2026): el backend real ya no manda `options` plano en
    // GET /formularios/vigente, manda `finalidades` agrupado (uno por cada finalidad, con su
    // propio `options` de un solo elemento) -- este mapeo se quedó leyendo solo la forma vieja
    // y por eso nunca aparecía ninguna finalidad adicional en el widget real. `options` queda
    // como fallback por si algún día vuelve a mandarse así.
    finalidadesAdicionales: (
      subAdicionales.finalidades?.flatMap((grupo) => grupo.options ?? [])
      ?? subAdicionales.options
      ?? []
    ).map((o) => ({
      code: o.code,
      id: o.id,
      label: o.label,
      description: o.description ?? '',
      linkedResource: o.linkedResource ?? null,
    })),
    acceptanceText: seccionLegal.acceptance?.text ?? '',
    acceptanceLinks: (seccionLegal.acceptance?.links ?? []).map((l) => ({
      id: l.id,
      label: l.label,
      url: l.url ?? null,
    })),
    footerLegalNote: raw.footer?.legalNotice?.text ?? '',
  };
}
